/**
 * Centralized API Service — Donor Portal
 */
import type { AxiosInstance } from 'axios';
import { createApiClient } from '@tms-phoenix/ui-vue';
import { config } from '@/core/config';

let apiInstance: AxiosInstance | null = null;

export function getApiClient(): AxiosInstance {
  if (!apiInstance) {
    apiInstance = createApiClient({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      getToken: () => {
        try {
          const raw = localStorage.getItem('pinia-auth');
          if (!raw) return null;
          return JSON.parse(raw)?.accessToken ?? null;
        } catch {
          return null;
        }
      },
      // ── 401 token-refresh callback ───────────────────────────────────────
      // Called by createApiClient when any authenticated request returns 401.
      // Lazy-imports auth store to break the circular dependency.
      onRefreshToken: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        const authStore = useAuthStore();
        const success = await authStore.refreshAccessToken();
        return success ? authStore.accessToken : null;
      },
      skipRefreshForUrls: ['/auth/login', '/auth/logout', '/auth/refresh'],
      // A refreshed token that still 401s means the session is invalid server-side —
      // force a client-side logout instead of retrying refresh again.
      onAuthFailure: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        useAuthStore().forceLogout();
      },
    });

    // Send x-tenant-hostname on every request so backend services can
    // resolve the tenant context (Cognito pool, DB schema, etc.).
    // Also inject loginType: 'donor' into request bodies.
    apiInstance.interceptors.request.use((requestConfig) => {
      if (requestConfig.headers) {
        requestConfig.headers['x-tenant-hostname'] = window.location.hostname;
      }

      // Append loginType: 'donor' to every request
      const method = requestConfig.method?.toLowerCase();
      if (method && ['post', 'put', 'patch'].includes(method)) {
        if (!requestConfig.data) {
          // Bodyless POST/PUT/PATCH — seed the body so the backend can detect the donor pool
          requestConfig.data = { loginType: 'donor' };
        } else if (typeof requestConfig.data === 'object' && !ArrayBuffer.isView(requestConfig.data) && !(requestConfig.data instanceof FormData)) {
          requestConfig.data = { ...requestConfig.data, loginType: 'donor' };
        }
      }

      if (method === 'get' || method === 'delete') {
        requestConfig.params = { ...requestConfig.params, loginType: 'donor' };
      }

      return requestConfig;
    });
  }

  return apiInstance;
}

export function resetApiClient(): void {
  apiInstance = null;
}
